import { createFileRoute } from "@tanstack/react-router";
import { loadStandalonePage } from "~/utils/content-fns";
import { BlockRenderer } from "~/components/blocks";
import {
  seo,
  seoLinks,
  eventJsonLd,
  breadcrumbJsonLd,
  type EventPlace,
  type SubEventInput,
} from "~/utils/seo";

/** Shapes of the bits of the `transform-agenda` block this route reads. */
interface AgendaSpeaker {
  name?: string;
  title?: string;
  company?: string;
  role?: string;
}
interface AgendaSession {
  title?: string;
  speakers?: AgendaSpeaker[];
  is_break?: boolean;
}
interface AgendaReception {
  name?: string;
  start?: string;
  end?: string;
  venue?: string;
  address?: string;
  copy?: string;
}
interface AgendaDay {
  title?: string;
  date?: string;
  sessions?: AgendaSession[];
  reception?: AgendaReception;
}

/** Pacific Daylight Time — the whole event falls before the November change. */
const TZ_OFFSET = "-07:00";

function isoAt(date?: string, hhmm?: string): string | null {
  if (!date || !hhmm) return null;
  return `${date}T${hhmm}:00${TZ_OFFSET}`;
}

/**
 * Derive the Event's `performer` list and the two reception `subEvent`s from
 * the agenda the page actually renders, rather than restating them in the
 * route. One source of truth: a speaker added to the agenda appears in the
 * structured data automatically, and the two can't drift.
 *
 * Deliberately no Event per session — see `eventJsonLd`.
 */
function deriveEventParts(days: AgendaDay[]) {
  const performers: Array<{ name: string; jobTitle?: string; company?: string }> = [];
  const seen = new Set<string>();
  const subEvents: SubEventInput[] = [];

  for (const day of days) {
    // Sessions and a reception are not alternatives — October 5 has both — so
    // every day is asked for each in turn rather than being classified as one
    // kind or the other.
    for (const session of (day.sessions || []).filter(
      (s) => s.title && !s.is_break,
    )) {
      for (const speaker of session.speakers || []) {
        if (!speaker.name || seen.has(speaker.name)) continue;
        seen.add(speaker.name);
        performers.push({
          name: speaker.name,
          ...(speaker.title ? { jobTitle: speaker.title } : {}),
          ...(speaker.company ? { company: speaker.company } : {}),
        });
      }
    }

    // A reception is a sub-event of the conference: its own time, its own
    // venue. `name` falls back to the day title, which is only right when the
    // day is nothing but the reception.
    const reception = day.reception;
    if (!reception) continue;
    const startDate = isoAt(day.date, reception.start);
    const name = reception.name || day.title;
    if (!name || !startDate) continue;
    subEvents.push({
      name,
      startDate,
      endDate: isoAt(day.date, reception.end),
      description: reception.copy?.trim() || null,
      location: reception.venue
        ? {
            name: reception.venue,
            street:
              reception.address?.replace(/,\s*San Francisco\s*$/i, "") || null,
            locality: "San Francisco",
            region: "CA",
            country: "US",
          }
        : null,
    });
  }

  return { performers, subEvents };
}

export const Route = createFileRoute("/c1-transform-2026/agenda")({
  loader: async () => {
    const page = await loadStandalonePage({
      data: { path: "c1-transform-2026-agenda.md" },
    });
    return {
      title: (page.title as string) || "",
      seo_title: page.seo_title as string | undefined,
      seo_description: page.seo_description as string | undefined,
      featured_image: page.featured_image as string | undefined,
      event: page.event as Record<string, any> | undefined,
      blocks: (page.blocks as any[]) || [],
    };
  },
  head: ({ loaderData }) => {
    const data = loaderData as any;
    const blocks: any[] = data?.blocks || [];
    const agenda = blocks.find((b) => b?.block === "transform-agenda");
    const event = data?.event;

    const scripts: Array<Record<string, unknown>> = [];

    if (event?.name && event?.start) {
      const { performers, subEvents } = deriveEventParts(
        (agenda?.days as AgendaDay[]) || [],
      );
      scripts.push(
        eventJsonLd({
          name: event.name,
          startDate: event.start,
          endDate: event.end,
          url: event.url || "/c1-transform-2026",
          description: event.description,
          image: data?.featured_image,
          location: (event.location || { name: "The Clancy" }) as EventPlace,
          subEvents,
          performers,
        }),
      );
    }

    const crumbs = breadcrumbJsonLd([
      { name: "C1 Transform 2026", url: "/c1-transform-2026" },
      { name: "Agenda" },
    ]);
    if (crumbs) scripts.push(crumbs);

    return {
      meta: seo({
        title: data?.seo_title || data?.title,
        description: data?.seo_description,
        image: data?.featured_image,
        url: "/c1-transform-2026/agenda",
      }),
      links: seoLinks({ url: "/c1-transform-2026/agenda" }),
      scripts,
    };
  },
  component: () => {
    const p = Route.useLoaderData() as any;
    return <BlockRenderer blocks={p.blocks || []} />;
  },
});
